import {
  mediaHelper,
  paletteHelper,
  radiusHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import styled from 'styled-components';
import allianzLogo from '../../../../../../assets/allianz.svg';
import { ALLIANZ_BLUE } from '../constants';
import { SingleCTABannerDetails } from '../GovernmentDataPartnerBannerWithDataV2';
import { WebLink } from '../../../../../../app-kit/components/WebLink/WebLink';
import { DataGetter, trackEvent } from '@rea-argonaut/analytics';
import { deepOmitTypeName } from '../../../../../../analytics/helpers/deepOmitTypeName';

const StyledCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column-reverse;
  border-radius: ${radiusHelper('medium')};
  overflow: hidden;

  ${mediaHelper('tabletMin')} {
    flex-direction: row;
    height: 160px;
  }
`;

const LogoContainer = styled.div`
  position: absolute;
  top: 34px;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${paletteHelper('backgroundPrimary')};
  width: 108px;
  height: 36px;
  z-index: 10;

  ${mediaHelper('tabletMin')} {
    top: 100px;
    left: auto;
    right: 0;
  }
`;

const Logo = styled.img`
  width: 84px;
  height: 20px;
`;

const BannerImage = styled.img`
  width: 100%;
  height: 100px;
  object-fit: cover;
  object-position: top;

  ${mediaHelper('tabletMin')} {
    width: 50%;
    height: 160px;
  }
`;

const BannerContentContainer = styled.div`
  background-color: ${ALLIANZ_BLUE};
  width: 100%;
  min-height: 144px;
  padding: ${spacingHelper('medium')};
  display: flex;
  flex-direction: column;

  ${mediaHelper('tabletMin')} {
    width: 50%;
    min-width: 320px;
    height: 160px;
    min-height: unset;
    padding: ${spacingHelper('large')};
  }
`;

const BannerTitle = styled.div`
  color: ${paletteHelper('backgroundPrimary')};
  ${typographyHelper('subtitle02')};
  margin-bottom: ${spacingHelper('twoExtraSmall')};

  ${mediaHelper('tabletMin')} {
    min-width: 255px;
  }
`;

const BannerDescription = styled.div`
  color: ${paletteHelper('backgroundPrimary')};
  ${typographyHelper('body02')};
  max-width: 353px;
  margin-bottom: ${spacingHelper('small')};

  ${mediaHelper('tabletMin')} {
    min-width: 255px;
  }
`;

const StyledWebLink = styled(WebLink)`
  color: ${ALLIANZ_BLUE};
  background-color: ${paletteHelper('backgroundPrimary')};

  &:hover,
  &:focus,
  &:focus-visible,
  &:active {
    background-color: ${paletteHelper('backgroundPrimary')};
    color: ${ALLIANZ_BLUE};
  }
`;

const Disclaimer = styled.div`
  ${typographyHelper('caption')};
  color: ${paletteHelper('textSecondary')};
  margin-top: ${spacingHelper('extraSmall')};
`;

const trackBannerClick = (linkTracking: SingleCTABannerDetails['linkTracking']): void => {
  if (!linkTracking) return;
  const event: DataGetter = () => deepOmitTypeName(linkTracking);
  trackEvent(event);
};

interface SingleCTABannerProps {
  bannerDetails: SingleCTABannerDetails;
  imageSource: string;
}

export const SingleCTABanner = ({ bannerDetails, imageSource }: SingleCTABannerProps) => {
  const { title, description, link, disclaimer, linkTracking } = bannerDetails;

  return (
    <>
      <StyledCard data-testid="government-data-partner-banner-v2">
        <LogoContainer>
          <Logo src={allianzLogo} alt="Allianz Logo" />
        </LogoContainer>
        <BannerContentContainer>
          <BannerTitle>{title}</BannerTitle>
          <BannerDescription>{description}</BannerDescription>
          <StyledWebLink
            url={link.url}
            isExternal={link.isExternal}
            title={link.title}
            variant="button-primary"
            tracking={link.tracking}
            onClick={() => trackBannerClick(linkTracking)}
          />
        </BannerContentContainer>
        <BannerImage src={imageSource} alt="Allianz Renewal Banner" />
      </StyledCard>
      <Disclaimer>{disclaimer}</Disclaimer>
    </>
  );
};
